import React, { useState, useEffect, useCallback } from "react";
import { Modal, ModalBody, Alert, Spinner } from "reactstrap";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCheckCircle, faTimes } from "@fortawesome/free-solid-svg-icons";
import { connect } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
  loginJWT,
  toggleJWTModal,
  requestPasswordReset,
  setLoginModalView,
} from "../../redux/authJWT/action";
import PasswordInput from "../common/PasswordInput";
import { language } from "../../languageProvider/language/language";
import { defaultLanguage } from "../../helper/utility";

const LoginModal = (props) => {
  const navigate = useNavigate();
  const { loginModalView } = props.authJWT;

  const [form, setForm] = useState({ email: "", password: "" });
  const [resetEmail, setResetEmail] = useState("");

  const onChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleKeyPress = useCallback(
    (e) => {
      if (e.key === "Enter" && props.authJWT.modalJwtOpen && loginModalView === "login") {
        document.getElementById("login_button")?.click();
      }
    },
    [props.authJWT.modalJwtOpen, loginModalView]
  );

  useEffect(() => {
    if (props.authJWT.modalJwtOpen) {
      document.addEventListener("keyup", handleKeyPress);
    }
    return () => document.removeEventListener("keyup", handleKeyPress);
  }, [props.authJWT.modalJwtOpen, handleKeyPress]);

  const handleLogin = (e) => {
    e.preventDefault();
    props.loginJWT(form);
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    if (resetEmail) props.requestPasswordReset(resetEmail);
  };

  const handleGoToRegister = () => {
    props.toggleJWTModal();
    navigate("/register");
  };

  const handleModalClosed = () => {
    setForm({ email: "", password: "" });
    setResetEmail("");
    props.setLoginModalView("login");
  };

  const getTitle = () => {
    switch (loginModalView) {
      case "forgotPassword": return language[defaultLanguage].resetta_psw;
      case "forgotPasswordSent": return language[defaultLanguage].email_successo;
      default: return "Login";
    }
  };

  return (
    <Modal
      autoFocus={false}
      toggle={() => props.toggleJWTModal()}
      onClosed={handleModalClosed}
      centered
      className="up-modal--sm"
      isOpen={props.authJWT.modalJwtOpen}
    >
      <ModalBody className="up-modal__body">
        {/* Header */}
        <div className="up-modal__header">
          <h3 className="up-modal__title">{getTitle()}</h3>
          <div className="up-modal__close" onClick={() => props.toggleJWTModal()}>
            <FontAwesomeIcon icon={faTimes} />
          </div>
        </div>

        {/* Login view */}
        {loginModalView === "login" && (
          <div style={{ padding: "18px 22px 22px" }}>
            <form onSubmit={handleLogin}>
              <div className="up-form__group">
                <label className="up-form__label">{language[defaultLanguage].email}</label>
                <input
                  className="up-form__input"
                  value={form.email}
                  id="email"
                  name="email"
                  type="email"
                  placeholder={language[defaultLanguage].email}
                  required
                  onChange={onChange}
                />
              </div>
              <div className="up-form__group">
                <label className="up-form__label">{language[defaultLanguage].password}</label>
                <PasswordInput
                  value={form.password}
                  id="password"
                  name="password"
                  placeholder={language[defaultLanguage].password}
                  required
                  onChange={onChange}
                />
              </div>

              {!!props.authJWT.error && (
                <Alert color="danger" className="py-2 text-center">
                  {props.authJWT.error}
                </Alert>
              )}

              <button
                className="up-btn up-btn--primary"
                type="submit"
                id="login_button"
                disabled={props.authJWT.loading}
                style={{ width: "100%", marginTop: 6, padding: "12px 0" }}
              >
                {props.authJWT.loading ? (
                  <Spinner size="sm" color="light" />
                ) : (
                  language[defaultLanguage].login
                )}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: 14 }}>
              <span
                style={{ color: "var(--primary-color)", cursor: "pointer", fontSize: "0.88rem" }}
                onClick={() => props.setLoginModalView("forgotPassword")}
              >
                {language[defaultLanguage].password_dimenticata}
              </span>
            </div>

            <div style={{ textAlign: "center", marginTop: 8, fontSize: "0.88rem", color: "#888" }}>
              {language[defaultLanguage].no_account}{" "}
              <span
                style={{ color: "var(--primary-color)", cursor: "pointer", fontWeight: 600 }}
                onClick={handleGoToRegister}
              >
                {language[defaultLanguage].registrati}
              </span>
            </div>
          </div>
        )}

        {/* Forgot password view */}
        {loginModalView === "forgotPassword" && (
          <div style={{ padding: "18px 22px 22px" }}>
            <p style={{ color: "#666", textAlign: "center", fontSize: "0.9rem", marginBottom: 14 }}>
              {language[defaultLanguage].inserisci_email_reset}
            </p>
            <form onSubmit={handleForgotPassword}>
              <div className="up-form__group">
                <label className="up-form__label">{language[defaultLanguage].email}</label>
                <input
                  className="up-form__input"
                  value={resetEmail}
                  type="email"
                  placeholder={language[defaultLanguage].email}
                  required
                  onChange={(e) => setResetEmail(e.target.value)}
                />
              </div>

              {!!props.authJWT.error && (
                <Alert color="danger" className="py-2 text-center">
                  {props.authJWT.error}
                </Alert>
              )}

              <button
                className="up-btn up-btn--primary"
                type="submit"
                disabled={props.authJWT.loading}
                style={{ width: "100%", marginTop: 6, padding: "12px 0" }}
              >
                {props.authJWT.loading ? (
                  <Spinner size="sm" color="light" />
                ) : (
                  language[defaultLanguage].invia_link_reset
                )}
              </button>
            </form>

            <div style={{ textAlign: "center", marginTop: 14 }}>
              <span
                style={{ color: "var(--primary-color)", cursor: "pointer", fontSize: "0.88rem" }}
                onClick={() => props.setLoginModalView("login")}
              >
                {language[defaultLanguage].torna_al_login}
              </span>
            </div>
          </div>
        )}

        {/* Email sent confirmation */}
        {loginModalView === "forgotPasswordSent" && (
          <div style={{ padding: "30px 22px", textAlign: "center" }}>
            <FontAwesomeIcon
              icon={faCheckCircle}
              style={{ color: "#28a745", fontSize: "3rem", marginBottom: 15 }}
            />
            <p style={{ color: "#666", fontSize: "0.95rem" }}>
              {language[defaultLanguage].email_inviata_reset}
            </p>
            <div style={{ marginTop: 14 }}>
              <span
                style={{ color: "var(--primary-color)", cursor: "pointer", fontSize: "0.88rem" }}
                onClick={() => props.setLoginModalView("login")}
              >
                {language[defaultLanguage].torna_al_login}
              </span>
            </div>
          </div>
        )}
      </ModalBody>
    </Modal>
  );
};

const mapStateToProps = (state) => ({
  authJWT: state.authJWT,
});

export default connect(mapStateToProps, {
  loginJWT,
  toggleJWTModal,
  requestPasswordReset,
  setLoginModalView,
})(LoginModal);
